"use client";

import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { Radio } from "lucide-react";
import { ForumCommentSection, type ForumCommentView } from "@/components/forum/forum-comment-section";

const LIVE_MATCH_STATUSES = new Set(["LIVE", "IN_PLAY", "PAUSED"]);

type MatchSnapshot = {
  competitionName?: string | null;
  status?: string | null;
  homeName: string;
  awayName: string;
  homeScore?: number | null;
  awayScore?: number | null;
} | null | undefined;

type LiveSnapshot = {
  comments: ForumCommentView[];
  commentCount: number;
  hasMoreComments: boolean;
  isLive: boolean;
  lastActivityAt: string;
  match: MatchSnapshot;
  totalRootComments: number;
};

function cadenceForThread(input: {
  isVisible: boolean;
  isLive: boolean;
  lastActivityAt: number;
  lastInteractionAt: number;
}) {
  if (!input.isVisible || !input.isLive) return null;

  const now = Date.now();
  const sinceActivity = now - input.lastActivityAt;
  const sinceInteraction = now - input.lastInteractionAt;

  if (sinceInteraction > 3 * 60 * 1000) return 45000;
  if (sinceActivity < 2 * 60 * 1000) return 12000;
  if (sinceActivity < 10 * 60 * 1000) return 20000;
  return 35000;
}

function isLiveMatchSnapshot(status: string | null | undefined, fallbackLive: boolean) {
  return Boolean(fallbackLive && LIVE_MATCH_STATUSES.has(status ?? ""));
}

function buildSnapshotKey(nextComments: ForumCommentView[], nextMatch: MatchSnapshot, nextLive: boolean) {
  const commentKey = nextComments
    .map((comment) => `${comment.id}:${comment.parentId ?? ""}:${comment.score}:${comment.currentUserVote}:${comment.hiddenAt ? 1 : 0}:${comment.removedAt ? 1 : 0}`)
    .join("|");
  const matchKey = nextMatch
    ? `${nextMatch.status ?? ""}:${nextMatch.homeScore ?? ""}:${nextMatch.awayScore ?? ""}:${nextMatch.homeName}:${nextMatch.awayName}`
    : "nomatch";
  return `${nextLive ? "1" : "0"}::${matchKey}::${commentKey}`;
}

export function LiveThreadExperience({
  threadId,
  currentUserId,
  isFollowing,
  comments: initialComments,
  isLive: initialIsLive,
  isLocked,
  lockedMessage,
  match: initialMatch,
  commentPages,
  hasMoreComments,
  chatMode = false,
  canModerateComments = false,
}: {
  threadId: string;
  currentUserId: string | null;
  isFollowing: boolean;
  comments: ForumCommentView[];
  isLive: boolean;
  isLocked?: boolean;
  lockedMessage?: string;
  match?: MatchSnapshot;
  commentPages: number;
  hasMoreComments: boolean;
  chatMode?: boolean;
  canModerateComments?: boolean;
}) {
  const previousIds = useRef<string[]>(initialComments.map((comment) => comment.id));
  const pollTimer = useRef<number | null>(null);
  const isPollingRef = useRef(false);
  const [comments, setComments] = useState(initialComments);
  const [isLive, setIsLive] = useState(() => isLiveMatchSnapshot(initialMatch?.status, initialIsLive));
  const [match, setMatch] = useState(initialMatch);
  const [newCommentCount, setNewCommentCount] = useState(0);
  const [highlightedCommentIds, setHighlightedCommentIds] = useState<string[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastActivityAt, setLastActivityAt] = useState(() => {
    const latestComment = initialComments[0];
    return latestComment ? new Date(latestComment.createdAt).getTime() : 0;
  });
  const [lastInteractionAt, setLastInteractionAt] = useState(0);
  const snapshotKeyRef = useRef(buildSnapshotKey(initialComments, initialMatch, isLiveMatchSnapshot(initialMatch?.status, initialIsLive)));

  useEffect(() => {
    const onVisibilityChange = () => setIsVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    const onUserActivity = () => setLastInteractionAt(Date.now());
    window.addEventListener("focus", onUserActivity);
    window.addEventListener("pointerdown", onUserActivity, { passive: true });
    window.addEventListener("keydown", onUserActivity);
    return () => {
      window.removeEventListener("focus", onUserActivity);
      window.removeEventListener("pointerdown", onUserActivity);
      window.removeEventListener("keydown", onUserActivity);
    };
  }, []);

  useEffect(() => {
    const currentIds = comments.map((comment) => comment.id);
    const addedIds = currentIds.filter((id) => !previousIds.current.includes(id));

    if (addedIds.length > 0) {
      setNewCommentCount((value) => value + addedIds.length);
      setHighlightedCommentIds(addedIds);
      setLastActivityAt(Date.now());
    }

    previousIds.current = currentIds;
  }, [comments]);

  const fetchSnapshot = useEffectEvent(async () => {
    if (isPollingRef.current) return;

    isPollingRef.current = true;
    setIsPolling(true);

    try {
      const response = await fetch(
        `/api/forum/threads/${threadId}/snapshot?commentPages=${commentPages}`,
        {
          cache: "no-store",
          headers: { accept: "application/json" },
        },
      );

      if (!response.ok) return;

      const snapshot = (await response.json()) as LiveSnapshot;
      const nextIsLive = isLiveMatchSnapshot(snapshot.match?.status, snapshot.isLive);
      const nextKey = buildSnapshotKey(snapshot.comments, snapshot.match, nextIsLive);
      if (snapshotKeyRef.current === nextKey) {
        return;
      }

      snapshotKeyRef.current = nextKey;
      setComments(snapshot.comments);
      setIsLive(nextIsLive);
      setMatch(snapshot.match);
      setLastActivityAt(new Date(snapshot.lastActivityAt).getTime());
    } catch {
      // Polling failure should not destabilize the thread UI.
    } finally {
      isPollingRef.current = false;
      setIsPolling(false);
    }
  });

  useEffect(() => {
    const cadence = cadenceForThread({
      isVisible,
      isLive,
      lastActivityAt,
      lastInteractionAt,
    });

    if (!cadence) {
      if (pollTimer.current) window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
      return;
    }

    pollTimer.current = window.setTimeout(() => {
      void fetchSnapshot();
    }, cadence);

    return () => {
      if (pollTimer.current) window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    };
  }, [isLive, isVisible, lastActivityAt, lastInteractionAt]);

  const pollingLabel = useMemo(() => {
    const cadence = cadenceForThread({
      isVisible,
      isLive,
      lastActivityAt,
      lastInteractionAt,
    });
    if (!cadence) return "élő frissítés szünetel";
    return cadence <= 15000 ? "gyors élő frissítés" : cadence <= 25000 ? "normál frissítés" : "lassabb háttérfrissítés";
  }, [isLive, isVisible, lastActivityAt, lastInteractionAt]);

  return (
    <div className="space-y-4">
      {isLive && match ? (
        <div className="sticky top-[68px] z-20 rounded-[24px] bg-[#F8F6F0]/98 p-4 shadow-[0_8px_22px_rgba(39,62,71,0.05)] ring-1 ring-black/5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-[0.16em] text-[#A4243B]">
                <Radio size={12} /> Live match thread
              </p>
              <p className="mt-1 text-sm font-semibold text-[#273E47]/70">
                {match.competitionName ?? "Élő mérkőzés"} · {match.status ?? "LIVE"} · {pollingLabel}
              </p>
            </div>
            <div className="rounded-[18px] bg-[#EEF0EA] px-4 py-3 text-center ring-1 ring-black/5">
              <p className="text-sm font-black text-[#273E47]">
                {match.homeName} {match.homeScore ?? 0} - {match.awayScore ?? 0} {match.awayName}
              </p>
              <p className="mt-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#273E47]/60">
                {comments.length} betöltve
                {hasMoreComments ? " · több régebbi komment külön oldalon" : ""}
                {isPolling ? " · frissítés..." : ""}
              </p>
            </div>
          </div>
          {newCommentCount > 0 ? (
            <button
              type="button"
              onClick={() => {
                setNewCommentCount(0);
                document.getElementById("forum-comments")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="mt-3 rounded-full bg-[#A4243B] px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-white transition hover:bg-[#273E47]"
            >
              {newCommentCount} új komment
            </button>
          ) : null}
        </div>
      ) : null}

      <div id="forum-comments">
        <ForumCommentSection
          threadId={threadId}
          currentUserId={currentUserId}
          isFollowing={isFollowing}
          comments={comments}
          isLocked={isLocked}
          lockedMessage={lockedMessage}
          highlightedCommentIds={highlightedCommentIds}
          newCommentCount={newCommentCount}
          isPolling={isPolling}
          chatMode={chatMode}
          canModerateComments={canModerateComments}
        />
      </div>
    </div>
  );
}
